import {ProductInterface} from "../../interfaces/catalog/ProductInterface";
import {FilterInterface} from "../../interfaces/catalog/FilterInterface";
import {hasProductStock} from "../../utils/stockLabel";

// Отбор в списке товаров ответа консультанта (ChatProductBrowser) — на странице, без вопроса к модели
export interface ChatFilterState {
    priceMin: number | null
    priceMax: number | null
    inStock: boolean
    brands: string[]
    properties: Record<number, string[]> // свойство → выбранные значения (любое из них)
}

export const EMPTY_FILTER: ChatFilterState = {priceMin: null, priceMax: null, inStock: false, brands: [], properties: {}};

export interface ChatFilterGroup {
    id: number
    name: string
    values: {value: string, count: number}[]
}

// Цена, по которой отбираем и которую видит покупатель: его цена, без неё — розничная
export function productPrice(product: ProductInterface): number | null {
    const price = product.client_price || product.current_price;

    return price ? price.amount / 100 : null;
}

// Границы слайдера цены: от самой низкой до самой высокой цены товаров списка, в целых рублях
export function priceRangeOf(products: ProductInterface[]): {min: number, max: number} {
    const prices = products.map(productPrice).filter((price): price is number => price !== null && price > 0);

    return prices.length ? {min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices))} : {min: 0, max: 0};
}

export function activeFilterCount(filter: ChatFilterState): number {
    return (filter.priceMin !== null || filter.priceMax !== null ? 1 : 0)
        + (filter.inStock ? 1 : 0)
        + (filter.brands.length ? 1 : 0)
        + Object.values(filter.properties).filter(values => values.length).length;
}

/**
 * Сначала товары в наличии (склад клиента, как в карточке), потом остальные; внутри групп — исходный порядок.
 * Товар, которого нет в каталоге (данные не пришли), — в конце.
 */
export function inStockFirst<T>(items: T[], productOf: (item: T) => ProductInterface | undefined): T[] {
    const rank = (item: T) => {
        const product = productOf(item);

        return product ? (hasProductStock(product) ? 0 : 1) : 2;
    };

    return items.map((item, index) => ({item, index, rank: rank(item)}))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(entry => entry.item);
}

/**
 * Товары, прошедшие отбор, в исходном порядке (порядок задал сервер: поиск — по названию, похожие — по сходству).
 * Разные свойства — все сразу, значения одного свойства — любое из выбранных.
 */
export function filterProducts(products: ProductInterface[], filter: ChatFilterState): ProductInterface[] {
    return products.filter(product => {
        const price = productPrice(product);
        if (filter.priceMin !== null && (price === null || price < filter.priceMin)) return false;
        if (filter.priceMax !== null && (price === null || price > filter.priceMax)) return false;
        if (filter.inStock && !hasProductStock(product)) return false;
        if (filter.brands.length && !filter.brands.includes(product.brand_name)) return false;

        return Object.entries(filter.properties).every(([id, values]) =>
            !values.length || (product.properties[id] || []).some(value => values.includes(value)));
    });
}

/**
 * Свойства для отбора: те, что в фильтрах сайта (show_in_filters), и только с двумя и больше значениями
 * у товаров списка — по одному значению отбирать нечего. Значения — с числом товаров, числа по возрастанию.
 */
export function filterGroups(products: ProductInterface[], filters: FilterInterface[]): ChatFilterGroup[] {
    return filters
        .filter(filter => filter.type.show_in_filters)
        .sort((a, b) => (a.type.filter_order ?? Infinity) - (b.type.filter_order ?? Infinity) || a.type.name.localeCompare(b.type.name))
        .map(filter => {
            const counts = new Map<string, number>();
            products.forEach(product => {
                new Set(product.properties[filter.type.id] || []).forEach(value => counts.set(value, (counts.get(value) || 0) + 1));
            });
            const values = Array.from(counts, ([value, count]) => ({value, count}))
                .sort((a, b) => a.value.localeCompare(b.value, 'ru', {numeric: true}));

            return {id: filter.type.id, name: filter.type.name.trim(), values};
        })
        .filter(group => group.values.length > 1);
}

export function brandsOf(products: ProductInterface[]): string[] {
    return Array.from(new Set(products.map(product => product.brand_name).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}
